import React from 'react';
import { Search, RefreshCw, Filter, Plus } from 'lucide-react';
import "@/styles/admin-common.css";
import ThemeSettings from './ThemeSettings';
import { Button } from "@/components/ui/button";

/**
 * Modern Admin Page Template Component
 * 
 * A reusable template for admin pages with consistent styling and layout.
 * Provides a professional, clean interface suitable for exam management systems.
 * 
 * LOCATION: /components/common/ModernPageTemplate.jsx
 * USAGE: Import from "@/components/common/ModernPageTemplate"
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {React.ReactNode} props.headerAction - Action button/element for the header
 * @param {React.ReactNode} props.children - Main content to be rendered
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.contentClassName - Additional CSS classes for content area
 * @param {Object} props.stats - Statistics object with total count
 * @param {Function} props.onRefresh - Refresh callback function
 * @param {Object} props.search - Search configuration object
 * @param {string} props.search.placeholder - Search input placeholder
 * @param {string} props.search.value - Search input value
 * @param {Function} props.search.onChange - Search input change handler
 * @param {Function} props.search.onSubmit - Search form submit handler
 * @param {Object} props.filter - Filter configuration object
 * @param {Function} props.filter.onFilterClick - Filter button click handler
 * @param {boolean} props.filter.isActive - Whether filter is currently active/open
 * @param {Object} props.createAction - Create action configuration object
 * @param {Function} props.createAction.onClick - Create button click handler
 * @param {string} props.createAction.tooltip - Create button tooltip text
 */
const ModernPageTemplate = ({ 
  title, 
  subtitle, // Keep for backward compatibility but won't be displayed
  headerAction, 
  children, 
  className = "",
  contentClassName = "", // New prop for content area custom classes
  stats = null,
  onRefresh = null,
  search = null,
  filter = null,
  createAction = null
}) => {
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await onRefresh();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      // Add a small delay to show the spinning animation
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  return (
    <div className={`admin-page-container admin-fade-in ${className}`}>
      {/* Unified Header with Stats */}
      <div className="admin-page-header">
        <div className="admin-header-top">
          <div>
            <h1 className="admin-page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {headerAction && headerAction}
            <ThemeSettings />
          </div>
        </div>
        
        {/* Statistics and Actions in same header - Always show if stats or onRefresh provided */}
        {(stats || onRefresh || search || filter || createAction) && (
          <div className="admin-header-bottom">
            <div className="admin-stats-section">
              {stats && (
                typeof stats === 'object' && stats.total !== undefined ? (
                  <>
                    <div className="admin-stat-chip admin-stat-total">
                      Total: {stats.total || 0}
                    </div>
                    {stats.customStats && stats.customStats}
                  </>
                ) : (
                  stats
                )
              )}
            </div>
            
            <div className="admin-actions-section">
              {/* Search Component */}
              {search && (
                <div className="admin-header-search">
                  <div className="admin-search-input-wrapper-header">
                    <input
                      type="search"
                      placeholder={search.placeholder || "Search..."}
                      className="admin-search-input-header"
                      value={search.value}
                      onChange={search.onChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          search.onSubmit && search.onSubmit(e);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="admin-search-icon-header-clickable"
                      onClick={(e) => search.onSubmit && search.onSubmit(e)}
                      title="Search"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Filter Button */}
              {filter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={filter.onFilterClick}
                  className={`admin-action-btn ${filter.isActive ? 'admin-filter-active' : ''}`}
                  title="Filter"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              )}
              
              {/* Create Button */}
              {createAction && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createAction.onClick}
                  className="admin-action-btn"
                  title={createAction.tooltip || "Create"}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
              
              {/* Refresh Button - Always available when onRefresh is provided */}
              {onRefresh && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className={`admin-action-btn ${isRefreshing ? 'animate-spin' : ''}`}
                  title="Refresh"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className={`admin-content-area ${contentClassName}`}>
        {children}
      </div>
    </div>
  );
};
/**
 * Modern Card Component for consistent card styling
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {React.ReactNode} props.icon - Icon for the card header
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.hover - Enable hover effects (default: true)
 */
export const ModernCard = ({ 
  title, 
  icon, 
  children, 
  className = "", 
  hover = true 
}) => {
  return (
    <div className={`admin-card ${hover ? 'admin-hover-lift' : ''} ${className}`}>
      {title && (
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            {icon && icon}
            {title}
          </h2>
        </div>
      )}
      <div className="admin-card-content">
        {children}
      </div>
    </div>
  );
};

/**
 * Modern Search Component for consistent search styling
 * 
 * @param {Object} props - Component props
 * @param {string} props.placeholder - Search input placeholder
 * @param {string} props.value - Search input value
 * @param {Function} props.onChange - Search input change handler
 * @param {Function} props.onSubmit - Search form submit handler
 * @param {string} props.buttonText - Search button text (default: "Search")
 */
export const ModernSearch = ({ 
  placeholder = "Search...", 
  value, 
  onChange, 
  onSubmit, 
  buttonText = "Search" 
}) => {
  return (
    <div className="admin-search-container">
      <form onSubmit={onSubmit} className="admin-search-form">
        <div className="admin-search-input-wrapper">
          <input
            type="search"
            placeholder={placeholder}
            className="admin-search-input"
            value={value}
            onChange={onChange}
          />
          <Search className="admin-search-icon w-4 h-4" />
        </div>
        <button type="submit" className="admin-search-button">
          {buttonText}
        </button>
      </form>
    </div>
  );
};
/**
 * Modern Action Button Component
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {React.ReactNode} props.icon - Button icon
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disabled state
 */
export const ModernButton = ({ 
  children, 
  onClick, 
  icon, 
  className = "", 
  disabled = false,
  ...props 
}) => {
  return (
    <button 
      className={`admin-action-button ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {icon && icon}
      {children}
    </button>
  );
};

/**
 * Modern Badge Component
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Badge content
 * @param {string} props.variant - Badge variant ('template' | 'language' | default)
 * @param {string} props.className - Additional CSS classes
 */
export const ModernBadge = ({ 
  children, 
  variant = "default", 
  className = "" 
}) => {
  return (
    <span className={`admin-badge ${variant} ${className}`}>
      {children}
    </span>
  );
};

/**
 * Modern Loading Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.text - Loading text (default: "Loading...")
 */
export const ModernLoading = ({ text = "Loading..." }) => {
  return (
    <div className="admin-loading-container">
      <div className="admin-loading-spinner"></div>
      {text}
    </div>
  );
};

// Re-export the pagination footer for convenience
export { default as ModernPaginationFooter } from './ModernPaginationFooter';

export default ModernPageTemplate;